import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { API_BASE_URL } from '../config';
import apiFetch from '../utils/apiFetch';

/**
 * 台数を読み取る動画を選ぶ画面。
 *
 * 店舗に紐づいた動画を**その場で再生しながら**選び、「解析に使う」で1つの
 * ランにする。**1本ずつのランにはしない** — 同じ店を2本撮れば同じ台が両方に
 * 映るので、本ごとに数えて足すと倍になる。数えているのは歩いた全体。
 *
 * 再生できることが条件で、体裁ではない。タイトルとサムネイルだけのチェック
 * ボックスは正直に押せない（紐付け画面への指摘がそのまま当てはまる）。
 * こちらは押すと金がかかるぶん、強く当てはまる。
 */

const RUN_STATUS = {
  queued: { label: '待機中', cls: 'secondary' },
  running: { label: '解析中', cls: 'info' },
  ready: { label: '採用待ち', cls: 'warning' },
  adopted: { label: '採用済み', cls: 'success' },
  rejected: { label: '却下', cls: 'dark' },
  failed: { label: '失敗', cls: 'danger' },
};

const fmtDuration = (sec) => {
  if (!sec) return '';
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
};

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString('ja-JP') : '');

/** 店舗をキーワードで探す。選ぶまで動画は出さない（店舗が主語なので）。 */
function StorePicker({ store, onPick }) {
  const [keyword, setKeyword] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const q = keyword.trim();
    if (!q) {
      setResults([]);
      return undefined;
    }
    const ac = new AbortController();
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const url = new URL(`${API_BASE_URL}/admin/stores`);
        url.searchParams.set('q', q);
        url.searchParams.set('limit', '10');
        const res = await apiFetch(url.toString(), { signal: ac.signal });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const body = await res.json();
        setResults(Array.isArray(body.data) ? body.data : []);
      } catch (e) {
        if (e.name !== 'AbortError') setResults([]);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => {
      clearTimeout(timer);
      ac.abort();
    };
  }, [keyword]);

  return (
    <div className="card mb-3">
      <div className="card-body">
        <label className="form-label fw-bold mb-1">店舗</label>
        {store ? (
          <div className="d-flex align-items-center gap-2">
            <span className="fs-5">{store.name}</span>
            <span className="text-muted small">
              {store.prefecture}
              {store.city}
            </span>
            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => onPick(null)}>
              変更
            </button>
          </div>
        ) : (
          <>
            <input
              className="form-control"
              placeholder="店名で検索"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
            />
            {loading && <div className="text-muted small mt-1">検索中…</div>}
            <ul className="list-group mt-2">
              {results.map((s) => (
                <li key={s.id} className="list-group-item list-group-item-action" role="presentation">
                  <button
                    type="button"
                    className="btn btn-link p-0 text-decoration-none"
                    onClick={() => onPick(s)}
                  >
                    {s.name}
                    <span className="text-muted small ms-2">
                      {s.prefecture}
                      {s.city}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
    </div>
  );
}

/** 1本。**再生できる形で出す**。YouTubeはiframe、自前撮影は署名付きURL。 */
function VideoRow({ video, checked, onToggle }) {
  const [open, setOpen] = useState(false);
  const last = video.lastRunStatus ? RUN_STATUS[video.lastRunStatus] : null;

  return (
    <div className={`border rounded p-2 mb-2 ${checked ? 'border-primary bg-light' : ''}`}>
      <div className="d-flex align-items-start gap-2">
        <input
          type="checkbox"
          className="form-check-input mt-1"
          checked={checked}
          onChange={onToggle}
          aria-label={video.title}
        />
        <div className="flex-grow-1">
          <div className="fw-bold">{video.title}</div>
          <div className="text-muted small">
            <span className={`badge bg-${video.source === 'youtube' ? 'danger' : 'primary'} me-2`}>
              {video.source === 'youtube' ? 'YouTube' : '自前撮影'}
            </span>
            {video.channel && <span className="me-2">{video.channel}</span>}
            {video.category && <span className="me-2">{video.category}</span>}
            {video.durationSec > 0 && <span className="me-2">{fmtDuration(video.durationSec)}</span>}
            <span>{fmtDate(video.publishedAt || video.capturedAt)}</span>
            {last && <span className={`badge bg-${last.cls} ms-2`}>解析{last.label}</span>}
          </div>
        </div>
        <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setOpen((v) => !v)}>
          {open ? '閉じる' : '見る'}
        </button>
      </div>

      {open && (
        <div className="mt-2">
          {video.source === 'youtube' ? (
            <iframe
              title={video.title}
              width="480"
              height="270"
              src={`https://www.youtube.com/embed/${video.youtubeId}`}
              allowFullScreen
              frameBorder="0"
            />
          ) : video.playbackUrl ? (
            // eslint-disable-next-line jsx-a11y/media-has-caption
            <video src={video.playbackUrl} controls width="480" preload="metadata" />
          ) : (
            <div className="text-muted small">再生できません（署名URLを発行できませんでした）</div>
          )}
        </div>
      )}
    </div>
  );
}

/** 機構（景品の取り方）の表示名。検出器が答える語彙そのまま。 */
const MECHANISM_LABEL = {
  claw: '爪でつかむ',
  hook: 'フック・棒',
  push: '押して落とす',
  timing: 'タイミング',
  roulette: 'ルーレット',
  other: 'その他',
};

/** 台種マスタの語彙。**「無かった台種」はこちらの語で出る**（歩いた動画だけが言える）。 */
const MACHINE_TYPE_LABEL = {
  hook: 'フック台',
  pusher: 'プッシャー',
  timing: 'タイミング台',
  roulette: 'ルーレット台',
};

/**
 * 1フレームと、その上の箱。
 *
 * **画像を固定枠に `object-fit: contain` で入れてはいけない。** 箱は 0..1 の
 * 正規化座標なので、左右に余白ができる入れ方をすると余白まで描画面として
 * 数えてしまう。PH1 で x が 38% に潰れたのはこれで、**同じ歪んだ写像で
 * 描き戻すので画面上は正しく見える**（的中率が0%になって初めて分かった）。
 * だから画像は block + width:100% で置き、箱は % でそのまま重ねる。
 */
function FrameCard({ frame }) {
  const boxes = Array.isArray(frame.detections) ? frame.detections : [];
  return (
    <div className="border rounded p-1" style={{ width: 260 }}>
      <div style={{ position: 'relative', lineHeight: 0 }}>
        {frame.imageUrl ? (
          <img src={frame.imageUrl} alt={`フレーム ${frame.frameSeq}`} style={{ display: 'block', width: '100%' }} />
        ) : (
          <div className="bg-light text-muted small p-3">画像を表示できません</div>
        )}
        {frame.usable &&
          boxes.map((b, i) => (
            <div
              key={i}
              title={MECHANISM_LABEL[b.mechanism] || b.mechanism || ''}
              style={{
                position: 'absolute',
                left: `${b.x * 100}%`,
                top: `${b.y * 100}%`,
                width: `${b.w * 100}%`,
                height: `${b.h * 100}%`,
                border: '2px solid #0d6efd',
                boxSizing: 'border-box',
              }}
            />
          ))}
      </div>
      <div className="small text-muted mt-1">
        {frame.videoSeq + 1}本目 / {frame.frameSeq}枚目 · {Math.round(frame.timestampSec)}秒
        {frame.usable ? (
          <span className="ms-1">· {boxes.length}箱</span>
        ) : (
          <span className="ms-1 text-danger">· 判定に使わず（{frame.reason || '理由なし'}）</span>
        )}
      </div>
    </div>
  );
}

/**
 * 解析結果と、採否。
 *
 * **数字だけを見せて「採用しますか」と聞く画面にはしない。** 聞いているのは
 * 「43台で合っているか」ではなく「この動画はフロアを一周しているか」で、
 * それは数字の中に答えが無い。通路を1本飛ばした動画から出た数は正しい数より
 * 必ず小さく、それでいて見た目は同じだけ自信がある。判断できるのは
 * フレームを見た人だけなので、フレームを出す。
 */
function RunDetail({ runId, onDone }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [count, setCount] = useState('');
  const [note, setNote] = useState('');
  // 既定 false。**チェックを外した状態は「言わない」**であって「なし」ではない
  const [adoptAbsent, setAdoptAbsent] = useState(false);

  const load = useCallback(async () => {
    setError('');
    try {
      const res = await apiFetch(`${API_BASE_URL}/admin/analysis-runs/${runId}`);
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.error?.message || `HTTP ${res.status}`);
      setData(body.data);
      setCount(body.data?.run?.machineCount != null ? String(body.data.run.machineCount) : '');
    } catch (e) {
      setError(e.message || 'Network Error');
    }
  }, [runId]);

  useEffect(() => {
    load();
  }, [load]);

  const act = async (path, payload) => {
    setBusy(true);
    setError('');
    try {
      const res = await apiFetch(`${API_BASE_URL}/admin/analysis-runs/${runId}/${path}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload || {}),
      });
      if (res.status !== 204) {
        const body = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(body?.error?.message || `HTTP ${res.status}`);
      } else if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }
      await load();
      onDone();
    } catch (e) {
      setError(e.message || 'Network Error');
    } finally {
      setBusy(false);
    }
  };

  if (!data) {
    return (
      <div className="card mb-3">
        <div className="card-body">
          {error ? <div className="alert alert-danger mb-0">{error}</div> : <span className="text-muted">読み込み中…</span>}
        </div>
      </div>
    );
  }

  const { run, frames = [] } = data;
  const st = RUN_STATUS[run.status] || { label: run.status, cls: 'secondary' };
  const unusable = frames.filter((f) => !f.usable).length;
  const decidable = run.status === 'ready';

  return (
    <div className="card mb-3">
      <div className="card-header d-flex align-items-center gap-2">
        <span className={`badge bg-${st.cls}`}>{st.label}</span>
        <span className="text-muted small">{fmtDate(run.createdAt)}</span>
        {run.detector && <span className="text-muted small">検出器: {run.detector}</span>}
        <button type="button" className="btn btn-sm btn-outline-secondary ms-auto" onClick={onDone}>
          閉じる
        </button>
      </div>
      <div className="card-body">
        {error && <div className="alert alert-danger">{error}</div>}
        {run.error && <div className="alert alert-warning">{run.error}</div>}

        <div className="mb-2">
          {run.machineCount != null ? (
            <span className="fs-5">
              確認できたのは <strong>{run.machineCount}</strong> 台
            </span>
          ) : (
            <span className="text-muted">まだ結果がありません</span>
          )}
          <div className="text-muted small">
            「この店に{run.machineCount ?? 0}台ある」ではありません。動画に写っていた数です。
            {frames.length > 0 && (
              <>
                {' '}
                {frames.length}枚のうち {frames.length - unusable}枚で判定
                {unusable > 0 && `（${unusable}枚はブレ・遮蔽などで判定に使わず）`}。
              </>
            )}
          </div>
        </div>

        <div className="mb-3 small">
          <div>
            見つけた取り方:{' '}
            {(run.machineTypesSeen || []).length > 0
              ? (run.machineTypesSeen || []).map((m) => MECHANISM_LABEL[m] || m).join(' / ')
              : 'なし'}
          </div>
          <div>
            この動画で見つからなかった台種:{' '}
            {(run.machineTypesAbsent || []).length > 0
              ? (run.machineTypesAbsent || []).map((t) => MACHINE_TYPE_LABEL[t] || t).join(' / ')
              : 'なし'}
            <span className="text-muted ms-1">
              （一周した動画だけが「この店にフックは無い」と言えます。採用すると店舗に「なし」として出ます）
            </span>
          </div>
        </div>

        {frames.length > 0 && (
          <div className="d-flex flex-wrap gap-2 mb-3" style={{ maxHeight: 520, overflowY: 'auto' }}>
            {frames.map((f) => (
              <FrameCard key={f.id} frame={f} />
            ))}
          </div>
        )}

        {decidable && (
          <div className="border-top pt-3">
            <div className="row g-2 align-items-end">
              <div className="col-auto">
                <label className="form-label small mb-0" htmlFor="adopt-count">
                  採用する台数
                </label>
                <input
                  id="adopt-count"
                  className="form-control"
                  style={{ width: 110 }}
                  value={count}
                  onChange={(e) => setCount(e.target.value.replace(/[^0-9]/g, ''))}
                />
              </div>
              <div className="col">
                <label className="form-label small mb-0" htmlFor="adopt-note">
                  根拠（フロアを一周しているか。必須）
                </label>
                <input
                  id="adopt-note"
                  className="form-control"
                  placeholder="例: 1Fのプライズ島を通路3本すべて通っている。2Fは未撮影"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                />
              </div>
            </div>
            {/* 既定は入れない。ランは見なかった機構を全部 absent で返すので、
                そのまま採ると通路1本ぶんの動画が「フックなし」と言い、
                公開検索から店を外す */}
            <div className="form-check mt-2">
              <input
                className="form-check-input"
                type="checkbox"
                id="adopt-absent"
                checked={adoptAbsent}
                onChange={(e) => setAdoptAbsent(e.target.checked)}
              />
              <label className="form-check-label small" htmlFor="adopt-absent">
                この動画は店内を一周しており、<strong>写っていない台の種類は「設置なし」</strong>として確定してよい
                <span className="d-block text-muted" style={{ fontSize: '0.75rem' }}>
                  入れないと台数だけが入ります。「なし」は検索から店を外すので、一周していない動画では付けないこと
                </span>
              </label>
            </div>
            <div className="d-flex gap-2 mt-2">
              <button
                type="button"
                className="btn btn-primary"
                disabled={busy || !note.trim() || !count}
                onClick={() =>
                  act('adopt', { count: Number(count), note: note.trim(), adoptAbsent })
                }
              >
                この台数を採用する
              </button>
              <button
                type="button"
                className="btn btn-outline-danger"
                disabled={busy}
                onClick={() => act('reject', { note: note.trim() })}
              >
                却下する
              </button>
              <span className="text-muted small align-self-center">
                却下も記録します（同じ動画の組で二度回さないため）
              </span>
            </div>
          </div>
        )}

        {(run.status === 'failed' || run.status === 'queued' || run.status === 'ready') && (
          <div className="mt-3">
            <button type="button" className="btn btn-sm btn-outline-secondary" disabled={busy} onClick={() => act('requeue')}>
              もう一度解析する
            </button>
            <span className="text-muted small ms-2">
              フレームを切り直して数え直します（結果は置き換わります）
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

export default function VideoAnalysis() {
  const [params, setParams] = useSearchParams();
  const [store, setStore] = useState(null);
  const [videos, setVideos] = useState([]);
  const [runs, setRuns] = useState([]);
  const [picked, setPicked] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [openRunId, setOpenRunId] = useState('');

  const storeId = store?.id || params.get('storeId') || '';

  const load = useCallback(async (id) => {
    if (!id) return;
    setLoading(true);
    setError('');
    try {
      const res = await apiFetch(`${API_BASE_URL}/admin/stores/${id}/videos`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const body = await res.json();
      setVideos(Array.isArray(body.data) ? body.data : []);
      setRuns(Array.isArray(body.meta?.runs) ? body.meta.runs : []);
    } catch (e) {
      setError(e.message || 'Network Error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(storeId);
    setPicked({});
  }, [storeId, load]);

  const pick = (s) => {
    setStore(s);
    setParams(s ? { storeId: s.id } : {});
  };

  const key = (v) => `${v.source}:${v.id}`;
  const chosen = videos.filter((v) => picked[key(v)]);

  const submit = async () => {
    setError('');
    setMessage('');
    try {
      const res = await apiFetch(`${API_BASE_URL}/admin/stores/${storeId}/analysis-runs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ videos: chosen.map((v) => ({ source: v.source, id: v.id })) }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.error?.message || `HTTP ${res.status}`);
      const run = body?.data?.run || body?.data;
      setMessage(
        body?.data?.queued === false
          ? `${chosen.length}本で解析ランを作りましたが、キューに入りませんでした。「もう一度解析する」で投入してください`
          : `${chosen.length}本で解析を始めました。結果が出たら「採用待ち」になります`,
      );
      if (run?.id) setOpenRunId(run.id);
      setPicked({});
      load(storeId);
    } catch (e) {
      setError(e.message || 'Network Error');
    }
  };

  return (
    <div>
      <h4 className="mb-1">動画から台数を読む</h4>
      <p className="text-muted small">
        店舗に紐づいた動画を見て、フロアを回っているものを選びます。
        <strong>選んだ動画はまとめて1回の解析になります</strong>
        （同じ台が2本に映るため、本ごとに数えて足すと倍になります）。
      </p>

      <StorePicker store={store} onPick={pick} />

      {error && <div className="alert alert-danger">{error}</div>}
      {message && <div className="alert alert-success">{message}</div>}

      {storeId && (
        <>
          {runs.length > 0 && (
            <div className="card mb-3">
              <div className="card-header">この店舗の解析</div>
              <ul className="list-group list-group-flush">
                {runs.map((r) => {
                  const st = RUN_STATUS[r.status] || { label: r.status, cls: 'secondary' };
                  return (
                    <li key={r.id} className="list-group-item d-flex align-items-center gap-2">
                      <span className={`badge bg-${st.cls}`}>{st.label}</span>
                      <span className="text-muted small">{fmtDate(r.createdAt)}</span>
                      {r.machineCount != null && (
                        <span>
                          確認できたのは <strong>{r.machineCount}</strong> 台
                        </span>
                      )}
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-primary ms-auto"
                        onClick={() => setOpenRunId((cur) => (cur === r.id ? '' : r.id))}
                      >
                        {openRunId === r.id ? '閉じる' : r.status === 'ready' ? 'フレームを見て採否を決める' : '中身を見る'}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          {openRunId && (
            <RunDetail
              runId={openRunId}
              onDone={() => {
                load(storeId);
              }}
            />
          )}

          {loading && <div className="text-muted">読み込み中…</div>}
          {!loading && videos.length === 0 && (
            <div className="alert alert-secondary">
              この店舗に紐づいた動画がありません。「店舗の紐付け」タブで先に紐付けてください。
            </div>
          )}

          {videos.map((v) => (
            <VideoRow
              key={key(v)}
              video={v}
              checked={!!picked[key(v)]}
              onToggle={() => setPicked((p) => ({ ...p, [key(v)]: !p[key(v)] }))}
            />
          ))}

          {videos.length > 0 && (
            <div className="sticky-bottom bg-white border-top py-2 d-flex align-items-center gap-3">
              <span>{chosen.length}本を選択中</span>
              <button
                type="button"
                className="btn btn-primary"
                disabled={chosen.length === 0}
                onClick={submit}
              >
                解析に使う
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
